import * as v from 'valibot';
import { BlastModelType } from 'app/business-logic/domain-models/Blasting';

const ActiveJobSchema = v.object({
  requesterId: v.string(),
  requesterName: v.string(),
  utcFromDateTime: v.string(),
  utcToDateTime: v.string(),
  utcLastUpdatedDateTime: v.string(),
  userId: v.string(),
  facilityAlias: v.string(),
  companyAlias: v.string(),
  id: v.string(),
  utcLastUpdatedTime: v.string(),
  progress: v.nullable(v.number()),
  hasCompleted: v.boolean(),
  jobSucceededCount: v.number(),
  jobFailedCount: v.number(),
  errors: v.array(v.unknown()),
});

export const GisPoint = v.object({ latitude: v.number(), longitude: v.number(), elevation: v.nullable(v.number()) });

export const ConcentrationRegionSchema = v.object({
  gisPoints: v.array(GisPoint),
});

export const ConcentrationDataSchema = v.object({
  value: v.number(),
  displayColor: v.string(),
  regions: v.array(ConcentrationRegionSchema),
});

export const PlumeDataSchema = v.object({
  concentrations: v.array(ConcentrationDataSchema),
  utcEndTime: v.string(),
});

const WindSpeedVectorSchema = v.object({
  latitude: v.number(),
  longitude: v.number(),
  windDirection: v.number(),
  windSpeed: v.number(),
});

const TemporalDataWindSpeedRangeSchema = v.object({
  displayColor: v.string(),
  maximumSpeed: v.number(),
  minimumSpeed: v.number(),
  windVectors: v.array(WindSpeedVectorSchema),
});

const WindDataSchema = v.object({
  windDataSpeedRanges: v.array(TemporalDataWindSpeedRangeSchema),
  utcEndTime: v.string(),
});

const TemporalDataWindSchema = v.object({
  type: v.literal('Wind'),
  data: v.array(v.nullish(WindDataSchema)),
});

export const BlastModelTypeSchema = v.enum(BlastModelType);

export const DispersionSimulationDataSchema = v.object({
  activeJobBatch: v.optional(v.nullable(ActiveJobSchema)),
  modelData: v.nullable(
    v.object({
      modelType: v.literal(BlastModelTypeSchema.enum.Dispersion),
      plumes: v.object({
        type: v.literal('Plume'),
        data: v.array(PlumeDataSchema),
      }),
      windData: TemporalDataWindSchema,
    })
  ),
});

export const FlyrockBoundarySchema = v.object({
  frontFaceDistanceInMetres: v.number(),
  backFaceDistanceInMetres: v.number(),
  color: v.string(),
  description: v.string(),
});

export const FlyrockSimulationModelSchema = v.object({
  modelType: v.literal(BlastModelTypeSchema.enum.Flyrock),
  azimuth: v.number(),
  flyrockBoundaries: v.array(FlyrockBoundarySchema),
  location: v.object({
    latitude: v.number(),
    longitude: v.number(),
  }),
});

export const FlyrockSimulationDataSchema = v.object({
  activeJobBatch: v.optional(v.nullable(ActiveJobSchema)),
  modelData: v.nullable(FlyrockSimulationModelSchema),
});

export const FumeManagementZoneSimulationDataSchema = v.object({
  activeJobBatch: v.optional(v.nullable(ActiveJobSchema)),
  modelData: v.nullable(
    v.object({
      modelType: v.literal(BlastModelTypeSchema.enum.FumeManagementZone),
      plumes: v.object({
        type: v.string(),
        data: v.array(PlumeDataSchema),
      }),
    })
  ),
});

export const IPpvThresholdSchema = v.object({
  ppvThreshold: v.number(),
  distanceInMetres: v.number(),
  color: v.string(),
});

export const GroundVibrationSimulationModelSchema = v.object({
  modelType: v.literal(BlastModelTypeSchema.enum.GroundVibration),
  ppvReadings: v.array(
    v.object({
      location: v.object({
        latitude: v.number(),
        longitude: v.number(),
      }),
      value: v.number(),
    })
  ),
  ppvThresholds: v.array(IPpvThresholdSchema),
  location: v.object({
    latitude: v.number(),
    longitude: v.number(),
  }),
});

export const GroundVibrationSimulationDataSchema = v.object({
  activeJobBatch: v.optional(v.nullable(ActiveJobSchema)),
  modelData: v.nullable(GroundVibrationSimulationModelSchema),
});

export const OverpressureSimulationModelDataSchema = v.object({
  image: v.string(),
  latitude: v.number(),
  longitude: v.number(),
  modelType: v.union([
    v.literal(BlastModelTypeSchema.enum.OverpressureContour),
    v.literal(BlastModelTypeSchema.enum.OverpressureGain),
  ]),
});

export const OverpressureSimulationDataSchema = v.object({
  activeJobBatch: v.optional(v.nullable(ActiveJobSchema)),
  modelData: v.nullable(OverpressureSimulationModelDataSchema),
});

export const GisTrackDataTimeStepSchema = v.object({
  latitudeStdDev: v.number(),
  longitudeStdDev: v.number(),
  elevationStdDev: v.number(),
  gisPoint: GisPoint,
  utcEndTime: v.string(),
});

export const PathSimulationDataSchema = v.object({
  activeJobBatch: v.optional(v.nullable(ActiveJobSchema)),
  modelData: v.nullable(
    v.object({
      modelType: v.literal(BlastModelTypeSchema.enum.Path),
      primaryTrack: v.object({
        gisTrackDataTimeSteps: v.array(GisTrackDataTimeStepSchema),
      }),
    })
  ),
});

export const OverlayFileInfoSchema = v.object({
  length: v.number(),
  name: v.string(),
  type: v.string(),
  utcLastModifiedTime: v.string(),
});

export const BlastJobStatusSchema = v.object({
  blastScenarioModelType: v.string(),
  blastingScenarioModelId: v.string(),
  error: v.nullable(v.string()),
  isComplete: v.boolean(),
  jobId: v.string(),
  progress: v.nullable(v.number()),
  utcCreatedTime: v.string(),
  utcFromDateTime: v.string(),
  utcLastUpdatedTime: v.string(),
  utcToDateTime: v.string(),
});

export const CreateScenarioResponseSchema = v.object({
  jobBatchId: v.string(),
  jobStatuses: v.array(BlastJobStatusSchema),
});
